import React, { useMemo } from 'react';
import VmkQuotePricingPage from './VmkQuotePricingPage';
import { VmkQuoteTemplateFront, VmkQuoteTemplateBack, VmkAgreementPage } from './VmkQuoteTemplatePages';
import { packQuotesIntoPages } from '../lib/vmkQuotePagePack';
import { getDrawingPageNumber, getTailPageNumber } from '../lib/vmkQuoteDocumentLayout';
import { getTotalQuotesAreaSqm } from '../lib/vmkQuoteTable';
import { enhanceQuoteDrawingSvg } from '../lib/enhanceQuoteDrawingSvg';
import { normalizeBackendQuoteDrawings } from '../lib/quoteSession';

const getQuoteLineTotal = (quote) => {
  const qty = quote.config?.quantity || quote.quantity || 1;
  if (quote.pricing?.subtotal != null) return Number(quote.pricing.subtotal);
  return (quote.pricing?.totalPrice || 0) * qty;
};

const formatPrice = (price) =>
  new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);

const getSvgWithoutBackground = (svgString) => {
  if (!svgString || typeof svgString !== 'string') return '';
  const cleaned = svgString.replace(/\bfill="#[fF]5[fF]5[fF]5"/g, 'fill="#FFF"');
  return enhanceQuoteDrawingSvg(cleaned);
};

/**
 * Off-screen VMK export (cover → drawings → investment → agreement) for view-quote PDF download.
 */
export const ViewQuotePdfDocument = ({
  quoteNumber,
  clientDetails = {},
  drawings = [],
  exportOptions = {},
  subtotal,
  signedAgreement = null,
  t,
}) => {
  const savedQuotes = useMemo(
    () => normalizeBackendQuoteDrawings({ drawings }),
    [drawings]
  );

  const includePricingInExport = exportOptions.includePricingInExport !== false;
  const includeTemplateInExport = exportOptions.includeTemplateInExport !== false;
  const showVmkTemplatePages = includeTemplateInExport && savedQuotes.length > 0;

  const totalPrice =
    typeof subtotal === 'number' && subtotal > 0
      ? subtotal
      : savedQuotes.reduce((sum, quote) => sum + getQuoteLineTotal(quote), 0);
  const totalArea = getTotalQuotesAreaSqm(savedQuotes);
  const advisorName = t('quote.template.defaultAdvisor');

  const quoteChunks = useMemo(
    () =>
      packQuotesIntoPages(savedQuotes, {
        includePricing: includePricingInExport,
        reserveTailSpace: includePricingInExport,
        tailReserveHeight: showVmkTemplatePages ? 95 : undefined,
      }),
    [savedQuotes, includePricingInExport, showVmkTemplatePages]
  );

  const drawingPageCount = quoteChunks.length;
  const templatePageNumbers = useMemo(() => {
    if (!showVmkTemplatePages) return null;
    return {
      cover: 1,
      proposal: 2,
      specs: 3,
      investment: getTailPageNumber(drawingPageCount, 0),
      warranty: getTailPageNumber(drawingPageCount, 1),
      process: getTailPageNumber(drawingPageCount, 2),
      sustainability: getTailPageNumber(drawingPageCount, 3),
      agreement: getTailPageNumber(drawingPageCount, 4),
    };
  }, [showVmkTemplatePages, drawingPageCount]);

  if (savedQuotes.length === 0) {
    return (
      <VmkAgreementPage
        pageNumber={1}
        clientDetails={clientDetails}
        signedAgreement={signedAgreement}
        t={t}
      />
    );
  }

  let globalFirstIndex = 0;
  const vmkPricingPages = quoteChunks.map((chunk, chunkIndex) => {
    const isLastPage = chunkIndex === quoteChunks.length - 1;
    const page = (
      <VmkQuotePricingPage
        key={chunk.map((q) => q.id).join('-') || chunkIndex}
        chunk={chunk}
        globalFirstIndex={globalFirstIndex}
        quoteNumber={quoteNumber}
        clientName={clientDetails.name}
        tableSvgData={null}
        getSvgWithoutBackground={getSvgWithoutBackground}
        getQuoteLineTotal={getQuoteLineTotal}
        formatPrice={formatPrice}
        t={t}
        isEditing={false}
        isExportingPDF
        removeQuote={() => {}}
        setTableSvgData={() => {}}
        savedQuotes={savedQuotes}
        totalPrice={totalPrice}
        compact={chunk.length > 1}
        showIntro={chunkIndex === 0}
        showTotals={isLastPage && includePricingInExport}
        showLegal={false}
        pageNumber={
          showVmkTemplatePages ? getDrawingPageNumber(chunkIndex) : chunkIndex + 1
        }
        showPricing={includePricingInExport}
        useBrandedLayout={showVmkTemplatePages}
      />
    );
    globalFirstIndex += chunk.length;
    return page;
  });

  return (
    <>
      {showVmkTemplatePages && templatePageNumbers && (
        <VmkQuoteTemplateFront
          pageNumbers={templatePageNumbers}
          quoteNumber={quoteNumber}
          clientDetails={clientDetails}
          advisorName={advisorName}
          savedQuotes={savedQuotes}
          t={t}
        />
      )}
      {vmkPricingPages}
      {showVmkTemplatePages && templatePageNumbers ? (
        <VmkQuoteTemplateBack
          pageNumbers={templatePageNumbers}
          quoteNumber={quoteNumber}
          clientDetails={clientDetails}
          totalPrice={totalPrice}
          totalAreaSqm={totalArea}
          formatPrice={formatPrice}
          getQuoteLineTotal={getQuoteLineTotal}
          savedQuotes={savedQuotes}
          signedAgreement={signedAgreement}
          t={t}
        />
      ) : (
        <VmkAgreementPage
          pageNumber={drawingPageCount + 1}
          clientDetails={clientDetails}
          signedAgreement={signedAgreement}
          t={t}
        />
      )}
    </>
  );
};
